import log from '@core/util/universal-logger';
import { load } from '@core/util/third-party-scripts';

const validEventTypes = ['view', 'link'];
const validBrands = ['mcom', 'bcom'];

function createTagPromise() {
  let _resolve;
  let _reject;

  const promise = new Promise(((resolve, reject) => {
    _resolve = resolve;
    _reject = reject;
  }));

  promise.resolve = _resolve;
  promise.reject = _reject;
  promise.isSettled = false;

  return promise;
}

const viewTagPromise = createTagPromise();
const tagLibPromise = createTagPromise();

function isAllOK(eventType, data) {
  if (eventType === undefined || data === undefined) {
    log.error('Tag Manager Exception : eventType and/or data missing!');
    return false;
  }

  if (validEventTypes.indexOf(eventType) === -1) {
    log.error('Tag Manager Exception : invalid eventType!');
    return false;
  }

  return true;
}

function isValidBrand(brand) {
  if (validBrands.indexOf(brand) >= 0) {
    return true;
  }

  log.error('Tag Manager Exception : invalid brand name!');
  return false;
}

function hasTealiumLoaded(eventType) {
  if (window.utag === undefined) {
    log.error('Tag Manager Exception: window.utag missing!');
    return false;
  }

  if (window.utag[eventType] === undefined) {
    log.error(`Tag Manager Exception: utag.${eventType} missing!`);
    return false;
  }

  return true;
}

function isAllOKToUpdateUtagData() {
  if (window.utag) {
    if (window.utag.data === undefined) {
      log.error('Tag Manager Exception: window.utag.data missing!');
      return false;
    }
  }
  return true;
}

function fireTags(eventType, data) {
  window.utag[eventType](data);
}

function tryResolvingLibPromise() {
  if (window.utag) {
    // tagLibPromise should be fulfilled when global tealium object is present.
    // we can resolve the tagLibPromise lib if it is not already resolved.
    if (!tagLibPromise.isSettled) {
      tagLibPromise.resolve('Tag manager library already loaded');
      tagLibPromise.isSettled = true;
    }
  }
}

function tryResolvingViewPromise() {
  if ((window.utag) && window.utag_data && (Object.keys(window.utag_data).length > 0) && !viewTagPromise.isSettled) {
    viewTagPromise.resolve('view tag already fired.');
    viewTagPromise.isSettled = true;
  }
}

function fireViewTag(data) {
  tryResolvingLibPromise();

  tagLibPromise.then(() => {
    fireTags('view', data);

    // Resolve the promise so that link tags can be fired.
    if (!viewTagPromise.isSettled) {
      viewTagPromise.resolve('view tag');
      viewTagPromise.isSettled = true;
    }
  }).catch(() => {
    if (!viewTagPromise.isSettled) {
      viewTagPromise.reject('Tag library promise got rejected, thus cant fire any tags.');
      viewTagPromise.isSettled = true;
    }
  });
}

function fireLinkTag(data) {
  tryResolvingLibPromise();
  tryResolvingViewPromise();

  viewTagPromise.then(() => {
    fireTags('link', data);
  });
}

function fireEventTag(eventType, data) {
  if (eventType === validEventTypes[0]) {
    fireViewTag(data);
  } else {
    fireLinkTag(data);
  }
}

function fireTag(eventType, data, options = { immediate: false }) {
  if (isAllOK(eventType, data)) {
    if (options.immediate) {
      if (hasTealiumLoaded(eventType)) {
        fireTags(eventType, data);
        tryResolvingLibPromise();
        tryResolvingViewPromise();
      }
    } else {
      fireEventTag(eventType, data, options);
    }
  }
}
function updateUtagDataFireTag(eventType, data) {
  if (isAllOK(eventType, data) && isAllOKToUpdateUtagData() && window.utag) {
    delete window.utag.data.adobe_page_name;
    fireEventTag(eventType, Object.assign(window.utag.data, data));
  }
}
async function loadTagLibrary(brand, environment, noview = false, profile = 'main') {
  window.utag_data = window.utag_data || {};
  if (noview) {
    // Disable the automatic tracking call on initial page load. https://docs.tealium.com/platforms/javascript/single-page-applications/
    window.utag_cfg_ovrd = window.utag_cfg_ovrd || {};
    window.utag_cfg_ovrd.noview = true;
  }
  if (window.utag === undefined) {
    if (!isValidBrand(brand)) {
      tagLibPromise.reject(new Error(`Invalid brand name. Possible values are : ${validBrands}`));
      tagLibPromise.isSettled = true;
    } else if (environment === undefined) {
      tagLibPromise.reject(new Error('Environment name is missing'));
      tagLibPromise.isSettled = true;
    } else {
      try {
        const brandName = (brand === 'mcom') ? 'macys' : brand;
        await load(`https://tags.tiqcdn.com/utag/${brandName}/${profile}/${environment}/utag.js`, {
          appendTo: 'body',
          async: true,
        });
        tagLibPromise.resolve('Tag manager library loaded');
        tagLibPromise.isSettled = true;
        tryResolvingViewPromise();
      } catch (e) {
        tagLibPromise.reject(new Error('Unable to load tag manager library'));
        tagLibPromise.isSettled = true;
      }
    }
  } else {
    tryResolvingLibPromise();
    tryResolvingViewPromise();
  }

  return tagLibPromise;
}
export {
  loadTagLibrary,
  updateUtagDataFireTag,
  fireTag,
};
